(function () {
  const productSelector = '.product[data-section-type="product"]:not(.product-footer)';
  const mediaContainerSelector = '.product__media-container';
  const mediaWrapperSelector = '[data-product-media-wrapper]';
  const thumbnailSelector = '[data-product-thumbnail]';
  const variantButtonSelector = '[dynamic-variant-button][data-option-value]';
  const progressiveImageSelector = 'img[data-al-progressive-image]';
  const primaryImageDecodeTimeout = 450;

  let activeRequest = null;
  let lastRequestedVariantId = null;
  let lastRenderedVariantId = null;
  const mediaCache = new Map();
  const prefetchRequests = new Map();
  const prefetchPromises = new Map();
  let activeInteractiveMedia = null;

  function getProductContainer() {
    return document.querySelector(productSelector);
  }

  function getCurrentVariantId(product) {
    const variantSelect = product && product.querySelector('[data-variant-select]');
    return variantSelect && variantSelect.value ? String(variantSelect.value) : '';
  }

  function getMediaId(variant) {
    return variant && variant.featured_media && variant.featured_media.id
      ? String(variant.featured_media.id)
      : '';
  }

  function hasMedia(product, mediaId) {
    return !!product.querySelector(`[data-media-item-id="${mediaId}"]:not([data-al-media-placeholder])`);
  }

  /* Bedding Clearance start */
  function isBeddingClearanceProduct(product) {
    return !!product && product.classList.contains('product--bedding-clearance');
  }
  /* Bedding Clearance end */

  function cacheMediaContainer(variantId, mediaContainer) {
    if (!variantId || !mediaContainer || mediaCache.has(String(variantId))) return;

    const cachedMediaContainer = mediaContainer.cloneNode(true);
    cachedMediaContainer.classList.remove('is-loading');
    delete cachedMediaContainer.dataset.alPdpMediaBound;
    cachedMediaContainer.querySelectorAll('[data-al-progressive-bound]').forEach((image) => {
      image.removeAttribute('data-al-progressive-bound');
    });

    mediaCache.set(String(variantId), cachedMediaContainer.outerHTML);
  }

  function getCachedMediaContainer(variantId) {
    const html = mediaCache.get(String(variantId));
    if (!html) return null;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
  }

  function ensureThemeSwitchTargets(product, variant) {
    const mediaId = getMediaId(variant);
    if (!mediaId) return;

    const mainGallery = product.querySelector('.product__media');
    if (mainGallery && !mainGallery.querySelector(`[data-media-item-id="${mediaId}"]`)) {
      const aspectRatio = mainGallery.querySelector('[data-aspect-ratio]')?.dataset.aspectRatio || 'natural';
      const mediaType = variant.featured_media.media_type || 'image';
      const wrapper = document.createElement('div');

      wrapper.className = 'product__media-item hidden';
      wrapper.dataset.mediaItemId = mediaId;
      wrapper.dataset.aspectRatio = aspectRatio;
      wrapper.setAttribute('data-product-media-wrapper', '');
      wrapper.setAttribute('data-al-media-placeholder', 'true');
      wrapper.innerHTML = `<div class="media media--aspect-ratio-${aspectRatio}" data-media-id="${mediaId}" data-media-type="${mediaType}"></div>`;

      mainGallery.appendChild(wrapper);
    }

    const thumbnails = product.querySelector('.product-thumbnails');
    if (thumbnails && !thumbnails.querySelector(`[data-thumbnail-id="${mediaId}"]`)) {
      const item = document.createElement('li');
      item.className = 'product-thumbnails__item';
      item.hidden = true;
      item.setAttribute('data-al-media-placeholder', 'true');
      item.innerHTML = `
        <button
          type="button"
          class="product-thumbnails__item-link"
          data-thumbnail-id="${mediaId}"
          data-product-thumbnail
          aria-label="Selected variant image"
        ></button>
      `;
      thumbnails.appendChild(item);
    }
  }

  function switchImage(product, mediaId) {
    const nextMedia = product.querySelector(`${mediaWrapperSelector}[data-media-item-id="${mediaId}"]`);
    if (!nextMedia) return;

    product.querySelectorAll(mediaWrapperSelector).forEach((media) => {
      media.classList.toggle('hidden', media !== nextMedia);
    });

    product.querySelectorAll(thumbnailSelector).forEach((thumbnail) => {
      thumbnail.classList.toggle('active', thumbnail.dataset.thumbnailId === mediaId);
    });

    const viewInSpaceButton = product.querySelector('[data-in-your-space]');
    const mediaElement = nextMedia.querySelector('.media');
    if (
      viewInSpaceButton &&
      mediaElement &&
      mediaElement.dataset.mediaType === 'model'
    ) {
      viewInSpaceButton.setAttribute('data-shopify-model3d-id', mediaElement.dataset.mediaId);
    }

    pauseActiveInteractiveMedia(mediaElement);
  }

  function bindMediaContainer(mediaContainer) {
    if (!mediaContainer || mediaContainer.dataset.alPdpMediaBound === 'true') return;

    mediaContainer.dataset.alPdpMediaBound = 'true';
    mediaContainer.addEventListener('click', (event) => {
      const thumbnail = event.target.closest(thumbnailSelector);
      if (!thumbnail || !mediaContainer.contains(thumbnail)) return;

      event.preventDefault();

      const product = mediaContainer.closest(productSelector);
      if (!product || !thumbnail.dataset.thumbnailId) return;

      switchImage(product, thumbnail.dataset.thumbnailId);
    });
  }

  function bindProgressiveImages(container) {
    if (!container) return;

    container.querySelectorAll(progressiveImageSelector).forEach((image) => {
      const markLoaded = () => {
        image.classList.add('is-loaded');
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
      } else if (image.dataset.alProgressiveBound !== 'true') {
        image.addEventListener('load', markLoaded, { once: true });
        image.addEventListener('lazyloaded', markLoaded, { once: true });
      }

      image.dataset.alProgressiveBound = 'true';
    });
  }

  function getVariantData() {
    const variantJson = document.querySelector('[data-variant-json]');
    if (!variantJson) return [];

    try {
      return JSON.parse(variantJson.textContent);
    } catch (error) {
      console.warn('[PDP media refresh] Could not parse variant JSON', error);
      return [];
    }
  }

  function getSelectedOptions(product) {
    return Array.from(product.querySelectorAll('[name^="options["]')).map((input) => input.value);
  }

  function getOptionIndex(button) {
    const optionGroup = button.closest('[data-product-option], [data-index]');
    const optionId = optionGroup && (optionGroup.dataset.productOption || optionGroup.dataset.index);
    const match = optionId && optionId.match(/option(\d+)/i);

    return match ? Number(match[1]) - 1 : -1;
  }

  function findVariantForButton(button) {
    const product = getProductContainer();
    if (!product) return null;

    const variants = getVariantData();
    if (!variants.length) return null;

    const optionIndex = getOptionIndex(button);
    if (optionIndex < 0) return null;

    const options = getSelectedOptions(product);
    options[optionIndex] = button.dataset.optionValue;

    return variants.find((variant) => {
      return variant.options && variant.options.every((option, index) => option === options[index]);
    }) || null;
  }

  function fetchMediaContainer(variant, signal) {
    const product = getProductContainer();
    if (!product) return Promise.resolve(null);

    const sectionId = product.dataset.sectionId;
    if (!sectionId) return Promise.resolve(null);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', sectionId);

    return fetch(url.toString(), {
      credentials: 'same-origin',
      signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newMediaContainer =
          doc.querySelector(`[data-section-id="${sectionId}"] ${mediaContainerSelector}`) ||
          doc.querySelector(mediaContainerSelector);

        if (!newMediaContainer) {
          throw new Error('Section response did not include a media container');
        }

        cacheMediaContainer(variant.id, newMediaContainer);

        return newMediaContainer;
      });
  }

  function getPrimaryMediaWrapper(mediaContainer, variant) {
    const mediaId = getMediaId(variant);

    return (
      mediaId && mediaContainer.querySelector(`${mediaWrapperSelector}[data-media-item-id="${mediaId}"]`)
    ) || mediaContainer.querySelector(`${mediaWrapperSelector}:not(.hidden)`) || mediaContainer.querySelector(mediaWrapperSelector);
  }

  function getPreloadWidth(currentMediaContainer) {
    const product = getProductContainer();
    const reference =
      currentMediaContainer ||
      (product && product.querySelector(mediaContainerSelector)) ||
      document.documentElement;
    const width = reference.getBoundingClientRect().width || window.innerWidth || 1200;
    const scaledWidth = Math.ceil(width * (window.devicePixelRatio || 1));

    return Math.min(1600, Math.max(720, scaledWidth));
  }

  function getPreloadImageSource(image, currentMediaContainer) {
    const source = {
      src: image.currentSrc || image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || image.getAttribute('data-sizes') || ''
    };
    const dataSrc = image.getAttribute('data-src');

    if (!source.srcset && dataSrc) {
      source.src = dataSrc.replace('{width}', getPreloadWidth(currentMediaContainer));
    }

    return source.src || source.srcset ? source : null;
  }

  function waitForPrimaryImage(newMediaContainer, variant, currentMediaContainer) {
    const primaryMedia = getPrimaryMediaWrapper(newMediaContainer, variant);
    const mediaElement = primaryMedia && primaryMedia.querySelector('.media');

    if (!mediaElement || mediaElement.dataset.mediaType !== 'image') {
      return Promise.resolve();
    }

    const image = primaryMedia.querySelector('.image__img');
    const source = image && getPreloadImageSource(image, currentMediaContainer);

    if (!source) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let settled = false;
      const preloadImage = new Image();
      const timer = window.setTimeout(() => resolveOnce(), primaryImageDecodeTimeout);

      function resolveOnce() {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve();
      }

      preloadImage.decoding = 'async';
      if (source.sizes) preloadImage.sizes = source.sizes;
      if (source.srcset) preloadImage.srcset = source.srcset;
      if (source.src) preloadImage.src = source.src;

      const decode = preloadImage.decode ? preloadImage.decode() : null;

      if (decode) {
        decode.then(resolveOnce).catch(resolveOnce);
      } else {
        preloadImage.onload = resolveOnce;
        preloadImage.onerror = resolveOnce;
      }
    });
  }

  function replaceMediaContainer(product, newMediaContainer, variant) {
    const currentMediaContainer = product && product.querySelector(mediaContainerSelector);
    if (!currentMediaContainer || !newMediaContainer) return Promise.resolve();

    return waitForPrimaryImage(newMediaContainer, variant, currentMediaContainer).then(() => {
      if (
        lastRequestedVariantId &&
        lastRequestedVariantId !== String(variant.id)
      ) {
        return;
      }

      if (!currentMediaContainer.isConnected) return;

      currentMediaContainer.replaceWith(newMediaContainer);
      bindMediaContainer(newMediaContainer);
      bindProgressiveImages(newMediaContainer);
      initInteractiveMedia(newMediaContainer);
      lastRenderedVariantId = String(variant.id);

      document.dispatchEvent(new CustomEvent('al:pdp-media-refreshed', {
        detail: {
          variant,
          mediaContainer: newMediaContainer
        }
      }));
    });
  }

  function pauseActiveInteractiveMedia(nextMedia) {
    if (!activeInteractiveMedia || activeInteractiveMedia.container === nextMedia) return;

    if (activeInteractiveMedia.player) {
      if (activeInteractiveMedia.type === 'video') {
        activeInteractiveMedia.player.pause();
      } else if (activeInteractiveMedia.type === 'external_video') {
        activeInteractiveMedia.player.pauseVideo();
      }
    } else if (activeInteractiveMedia.viewer) {
      activeInteractiveMedia.container.classList.remove('model-active');
      activeInteractiveMedia.viewer.pause();
    }

    activeInteractiveMedia = null;
  }

  function initInteractiveMedia(mediaContainer) {
    const elements = Array.from(mediaContainer.querySelectorAll('[data-interactive]'));
    if (!elements.length || !window.Shopify || !window.Shopify.loadFeatures) return;

    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0'
      },
      {
        name: 'shopify-xr',
        version: '1.0'
      },
      {
        name: 'video-ui',
        version: '1.0'
      }
    ], () => {
      elements.forEach(initInteractiveElement);
    });
  }

  function initInteractiveElement(element) {
    if (element.dataset.alInteractiveReady === 'true') return;

    const mediaId = element.dataset.mediaId;
    const mediaType = element.dataset.mediaType;
    const media = element.children[0];

    if (!mediaId || !mediaType || !media) return;

    const instance = {
      id: mediaId,
      type: mediaType,
      container: element,
      media
    };

    if (mediaType === 'video' && window.Shopify && window.Shopify.Plyr) {
      instance.player = new window.Shopify.Plyr(media, {
        loop: {
          active: element.dataset.loop === 'true'
        }
      });

      instance.player.on('playing', () => {
        pauseActiveInteractiveMedia(element);
        activeInteractiveMedia = instance;
      });
    } else if (mediaType === 'external_video' && window.YT && window.YT.Player) {
      instance.player = new window.YT.Player(media);
      instance.player.addEventListener('onStateChange', (event) => {
        if (event.data === 1) {
          pauseActiveInteractiveMedia(element);
          activeInteractiveMedia = instance;
        }
      });
    } else if (mediaType === 'model' && window.Shopify && window.Shopify.ModelViewerUI) {
      const modelViewer = element.querySelector('model-viewer');
      const poster = element.querySelector('.model-poster');

      if (!modelViewer) return;

      instance.viewer = new window.Shopify.ModelViewerUI(modelViewer);

      if (poster) {
        poster.addEventListener('click', (event) => {
          event.preventDefault();
          pauseActiveInteractiveMedia(element);
          instance.viewer.play();
          element.classList.add('model-active');
          activeInteractiveMedia = instance;
          setTimeout(() => modelViewer.focus(), 300);
        });
      }
    } else {
      return;
    }

    element.dataset.alInteractiveReady = 'true';
  }

  function prefetchMediaForVariant(variant) {
    if (!variant || !variant.id || !variant.featured_media) return;

    const product = getProductContainer();
    if (!product) return;

    const variantId = String(variant.id);
    const mediaId = getMediaId(variant);
    if (
      !mediaId ||
      mediaCache.has(variantId) ||
      prefetchRequests.has(variantId) ||
      (!isBeddingClearanceProduct(product) && hasMedia(product, mediaId))
    ) {
      return;
    }

    const request = new AbortController();
    prefetchRequests.set(variantId, request);

    const promise = fetchMediaContainer(variant, request.signal);
    prefetchPromises.set(variantId, promise);

    promise
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('[PDP media prefetch]', error);
        }
      })
      .finally(() => {
        prefetchRequests.delete(variantId);
        prefetchPromises.delete(variantId);
      });
  }

  function refreshMediaForVariant(variant) {
    const product = getProductContainer();
    if (!product) return;

    const mediaId = getMediaId(variant);
    if (!mediaId) return;
    lastRequestedVariantId = String(variant.id);

    bindMediaContainer(product.querySelector(mediaContainerSelector));

    /* Bedding Clearance start */
    if (!isBeddingClearanceProduct(product) && hasMedia(product, mediaId)) {
      lastRenderedVariantId = String(variant.id);
      return;
    }
    /* Bedding Clearance end */

    /* Bedding Clearance start */
    if (!isBeddingClearanceProduct(product)) {
      ensureThemeSwitchTargets(product, variant);
    }
    /* Bedding Clearance end */

    const mediaContainer = product.querySelector(mediaContainerSelector);
    if (!mediaContainer) return;

    cacheMediaContainer(lastRenderedVariantId || getCurrentVariantId(product), mediaContainer);

    const cachedMediaContainer = getCachedMediaContainer(variant.id);
    if (cachedMediaContainer) {
      mediaContainer.classList.add('is-loading');

      requestAnimationFrame(() => {
        replaceMediaContainer(product, cachedMediaContainer, variant);
      });

      return;
    }

    const pendingPrefetch = prefetchPromises.get(String(variant.id));
    if (pendingPrefetch) {
      if (activeRequest) {
        activeRequest.abort();
      }

      lastRequestedVariantId = String(variant.id);
      mediaContainer.classList.add('is-loading');

      pendingPrefetch
        .then((newMediaContainer) => {
          if (lastRequestedVariantId !== String(variant.id)) return;

          const currentProduct = getProductContainer();
          return replaceMediaContainer(currentProduct, newMediaContainer, variant);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.warn('[PDP media refresh]', error);
          }
        })
        .finally(() => {
          const currentProduct = getProductContainer();
          const currentMediaContainer = currentProduct && currentProduct.querySelector(mediaContainerSelector);
          currentMediaContainer && currentMediaContainer.classList.remove('is-loading');
        });

      return;
    }

    if (activeRequest) {
      activeRequest.abort();
    }

    activeRequest = new AbortController();
    const request = activeRequest;

    mediaContainer.classList.add('is-loading');

    fetchMediaContainer(variant, request.signal)
      .then((newMediaContainer) => {
        if (lastRequestedVariantId !== String(variant.id)) return;

        const currentProduct = getProductContainer();
        return replaceMediaContainer(currentProduct, newMediaContainer, variant);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('[PDP media refresh]', error);
        }
      })
      .finally(() => {
        const currentProduct = getProductContainer();
        const currentMediaContainer = currentProduct && currentProduct.querySelector(mediaContainerSelector);
        currentMediaContainer && currentMediaContainer.classList.remove('is-loading');

        if (activeRequest === request) {
          activeRequest = null;
        }
      });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const product = getProductContainer();
    const mediaContainer = product && product.querySelector(mediaContainerSelector);

    bindMediaContainer(mediaContainer);
    bindProgressiveImages(mediaContainer);

    lastRenderedVariantId = getCurrentVariantId(product);
    cacheMediaContainer(lastRenderedVariantId, mediaContainer);
  });

  document.addEventListener('product:variant-change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    refreshMediaForVariant(variant);
  });

  document.addEventListener('mouseover', (event) => {
    const button = event.target.closest(variantButtonSelector);
    if (!button) return;

    const variant = findVariantForButton(button);
    prefetchMediaForVariant(variant);
  });

  document.addEventListener('focusin', (event) => {
    const button = event.target.closest(variantButtonSelector);
    if (!button) return;

    const variant = findVariantForButton(button);
    prefetchMediaForVariant(variant);
  });
})();
